import type { LucideIcon } from "lucide-react";

import * as React from "react";

import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import { CircleHelp, Database } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { isTailwindClassName } from "replo-utils/lib/tailwind-classname";

type BaseBadgeProps = {
  isFilled?: boolean;
  onClick?: () => void;
  layoutClassName?: string;
  UNSAFE_className?: string;
};

type ImageBadgeProps = BaseBadgeProps & {
  type: "image";
  src?: string;
};

type IconBadgeProps = BaseBadgeProps & {
  type: "icon";
  icon: LucideIcon | React.ReactNode;
};

type DynamicDataBadgeProps = BaseBadgeProps & {
  type: "dynamicData";
};

type ColorBadgeProps = BaseBadgeProps & {
  type: "color";
  foregroundColor?: string;
  backgroundColor: string;
};

type VideoBadgeProps = BaseBadgeProps & {
  type: "video";
  src?: string;
};

type CharacterBadgeProps = BaseBadgeProps & {
  type: "character";
  character: string;
};

type UnknownBadgeProps = BaseBadgeProps & {
  type: "unknown";
};

type BadgeProps =
  | ImageBadgeProps
  | IconBadgeProps
  | DynamicDataBadgeProps
  | ColorBadgeProps
  | VideoBadgeProps
  | CharacterBadgeProps
  | UnknownBadgeProps;

const Badge = (props: BadgeProps) => {
  const content = exhaustiveSwitch(props)({
    character: (props) => {
      const { character } = props;
      return <div className="text-2xs text-white">{character}</div>;
    },
    image: (props) => {
      if (props.src) {
        return (
          <img
            src={props.src}
            alt="Preview Image"
            className="h-full w-full object-cover object-center"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        );
      }
      return null;
    },
    icon: (props) => {
      const { icon } = props;
      if (typeof icon === "function") {
        const Icon = icon;
        return <Icon />;
      }
      return icon;
    },
    dynamicData: () => <Database className="text-white" size={12} />,
    color: (props) => {
      const {
        backgroundColor,
        foregroundColor = "text-muted",
        isFilled,
      } = props;

      const textColorStyle = isTailwindClassName(foregroundColor, "text")
        ? foregroundColor
        : undefined;
      const backgroundColorStyle =
        isFilled && !isTailwindClassName(backgroundColor, "bg")
          ? backgroundColor
          : undefined;

      return (
        <div
          className={twMerge(
            "h-full w-full",
            isFilled &&
              isTailwindClassName(backgroundColor, "bg") &&
              backgroundColor,
            isTailwindClassName(foregroundColor, "text") && foregroundColor,
          )}
          style={{
            // NOTE (Sebas, 2024-09-29): These styles are necessary in case we don't pass a background color
            // from tailwind.
            background: backgroundColorStyle,
            color: textColorStyle,
          }}
        />
      );
    },
    video: (props) => {
      return (
        <video
          className="h-full w-full object-cover object-center"
          src={props.src ?? undefined}
        />
      );
    },
    unknown: () => <CircleHelp className="text-muted" size={12} />,
  });

  const shouldHaveBorder = !["dynamicData", "icon", "character"].includes(
    props.type,
  );
  return (
    <div
      className={twMerge(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm",
        shouldHaveBorder && "border border-border",
        Boolean(props.onClick) && "cursor-pointer",
        props.type === "dynamicData" && "bg-purple text-white",
        ["image", "color"].includes(props.type) && "overflow-hidden",
        props.type === "character" && "bg-primary",
        props.layoutClassName,
        props.UNSAFE_className,
      )}
      role={
        props.type === "dynamicData" || !props.onClick ? undefined : "button"
      }
      onClick={props.onClick}
    >
      {content}
    </div>
  );
};

export { Badge };
